if (!customElements.get('slider-with-column')) {
  class SliderWithColumn extends HTMLElement {
    constructor() {
      super();
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleDotClick = this.handleDotClick.bind(this);
      this.scrollFrame = null;
      this.resizeFrame = null;
    }

    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.dotsContainer = this.querySelector('[data-slider-dots]');
      this.slides = this.track ? Array.from(this.track.querySelectorAll('[data-slider-slide]')) : [];

      if (!this.track || !this.dotsContainer || this.slides.length === 0) return;

      this.buildDots();
      this.updateActiveDot();

      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      this.dotsContainer.addEventListener('click', this.handleDotClick);
      window.addEventListener('resize', this.handleResize, { passive: true });
    }

    disconnectedCallback() {
      if (this.track) this.track.removeEventListener('scroll', this.handleScroll);
      if (this.dotsContainer) this.dotsContainer.removeEventListener('click', this.handleDotClick);
      window.removeEventListener('resize', this.handleResize);
    }

    getColumnsPerView() {
      const w = window.innerWidth;
      if (w >= 1100) return 3;
      if (w >= 750) return 2;
      return 1;
    }

    getPageCount() {
      const cols = this.getColumnsPerView();
      return Math.max(1, Math.ceil(this.slides.length / cols));
    }

    buildDots() {
      const pages = this.getPageCount();
      const carouselLabel = this.getAttribute('aria-label') || 'Carousel';

      this.dotsContainer.setAttribute('role', 'tablist');
      this.dotsContainer.setAttribute('aria-label', carouselLabel + ' pagination');

      if (pages <= 1) {
        this.dotsContainer.innerHTML = '';
        this.dotsContainer.hidden = true;
        return;
      }
      this.dotsContainer.hidden = false;

      let html = '';
      for (let i = 0; i < pages; i++) {
        html +=
          '<button type="button" class="abt-who__dot" data-slider-dot="' +
          i +
          '" role="tab" aria-label="Go to slide ' +
          (i + 1) +
          ' of ' +
          pages +
          '"></button>';
      }
      this.dotsContainer.innerHTML = html;
      this.dots = Array.from(this.dotsContainer.querySelectorAll('[data-slider-dot]'));
    }

    handleResize() {
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.buildDots();
        this.updateActiveDot();
      });
    }

    handleScroll() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.updateActiveDot());
    }

    handleDotClick(e) {
      const target = e.target.closest('[data-slider-dot]');
      if (!target) return;
      const index = parseInt(target.getAttribute('data-slider-dot'), 10);
      this.goToPage(index);
    }

    getPageScrollTarget(pageIndex) {
      const cols = this.getColumnsPerView();
      const slideIndex = Math.min(pageIndex * cols, this.slides.length - 1);
      const slide = this.slides[slideIndex];
      if (!slide) return 0;

      const trackRect = this.track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const slideContentOffset = slideRect.left - trackRect.left + this.track.scrollLeft;

      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      return Math.max(0, Math.min(slideContentOffset, maxScroll));
    }

    goToPage(pageIndex) {
      const target = this.getPageScrollTarget(pageIndex);
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({
        left: target,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }

    updateActiveDot() {
      if (!this.dots || this.dots.length === 0) return;

      const scrollLeft = this.track.scrollLeft;
      const trackRect = this.track.getBoundingClientRect();

      let activePage = 0;
      let smallestDistance = Infinity;

      for (let i = 0; i < this.dots.length; i++) {
        const target = this.getPageScrollTarget(i);
        const distance = Math.abs(target - scrollLeft);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          activePage = i;
        }
      }

      this.dots.forEach((dot, i) => {
        if (i === activePage) {
          dot.setAttribute('aria-current', 'true');
          dot.setAttribute('aria-selected', 'true');
          dot.setAttribute('tabindex', '0');
          dot.classList.add('is-active');
        } else {
          dot.removeAttribute('aria-current');
          dot.setAttribute('aria-selected', 'false');
          dot.setAttribute('tabindex', '-1');
          dot.classList.remove('is-active');
        }
      });

      this.slides.forEach((slide) => {
        const slideRect = slide.getBoundingClientRect();
        const isVisible =
          slideRect.right > trackRect.left + 1 && slideRect.left < trackRect.right - 1;
        slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
        slide.querySelectorAll('a, button').forEach((el) => {
          el.setAttribute('tabindex', isVisible ? '0' : '-1');
        });
      });
    }
  }

  customElements.define('slider-with-column', SliderWithColumn);
}
